import { c as _c } from "react/compiler-runtime";
// Chats (Michael, 2026-10-25: like Telegram): one list, the newest message first; a row is an avatar, the chat's
// name, the time of its last message, the message itself and the unread count. With a space picked (the nav's scope)
// only its chats and those of the spaces nested under it, a nested one's badged with its space; on All every chat is
// badged with its space. Which threads, their order and unread counts are the core's (chat.inbox, chat/core),
// watched over the local replica. A thread opens as a modal (?thread=<id>: the sheet on phones, a page after this one
// on desktop).
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type Thread } from "@/api";
import { Empty } from "@/components/empty";
import { ErrorLine } from "@/components/error-line";
import { Initials } from "@/components/initials";
import { useBoardFilter } from "@/lib/hooks";

/** Telegram's stamps: today the time, this week the day, else the date. */
function stamp(at: string | null): string {
  if (!at) return "";
  const d = new Date(at);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit"
  });
  if (now.getTime() - d.getTime() < 6 * 86_400_000) return d.toLocaleDateString([], {
    weekday: "short"
  });
  return d.toLocaleDateString([], {
    day: "numeric",
    month: "short",
    ...(d.getFullYear() !== now.getFullYear() ? {
      year: "2-digit"
    } : {})
  });
}
export function ChatsPage(t0) {
  const $ = _c(38);
  const {
    selected,
    onOpen
  } = t0;
  const {
    t
  } = useTranslation();
  const [filter] = useBoardFilter();
  const space = filter.project;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      data: null,
      error: null
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [list, setList] = useState(t1);
  let t2;
  let t3;
  if ($[1] !== space) {
    t2 = () => {
      setList({
        data: null,
        error: null
      });
      return api.chat.inbox(space, (data, error) => setList(o => ({
        data: data ?? o.data,
        error
      })));
    };
    t3 = [space];
    $[1] = space;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] !== list.data) {
    t4 = list.data ?? [];
    $[4] = list.data;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const threads = t4;
  let t5;
  if ($[6] !== t) {
    t5 = t("nav.chats");
    $[6] = t;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = !list.data && !list.error || undefined;
  let t7;
  if ($[8] !== t) {
    t7 = t("nav.chats");
    $[8] = t;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t7) {
    t8 = <div className="head" data-t="head"><h1 data-t="title">{t7}</h1></div>;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== onOpen || $[13] !== selected || $[14] !== space || $[15] !== t || $[16] !== threads) {
    let t10;
    if ($[18] !== onOpen || $[19] !== selected || $[20] !== space || $[21] !== t) {
      t10 = x => {
        const name = x.general ? x.spaceName ?? x.title : x.title || t("chat.thread");
        const badge = !x.general && x.spaceName && x.spaceId !== space ? x.spaceName : null;
        return <li key={x.id}><button type="button" className="chat-row" data-t="thread-row" data-id={x.id} aria-pressed={selected === x.id} onClick={() => onOpen(x.id)}><Initials name={name} /><span className="chat-main"><span className="chat-top"><span className="chat-name">{name}</span>{badge && <span className="chat-badge">{badge}</span>}<time className="chat-time" dateTime={x.lastAt ?? undefined}>{stamp(x.lastAt)}</time></span><span className="chat-bottom"><span className="chat-last">{x.lastText ?? t("chat.noMessages")}</span>{x.unread > 0 && <span className="chat-unread">{x.unread}</span>}</span></span></button></li>;
      };
      $[18] = onOpen;
      $[19] = selected;
      $[20] = space;
      $[21] = t;
      $[22] = t10;
    } else {
      t10 = $[22];
    }
    t9 = threads.map(t10);
    $[12] = onOpen;
    $[13] = selected;
    $[14] = space;
    $[15] = t;
    $[16] = threads;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[23] !== t9) {
    t10 = <ul className="chat-list">{t9}</ul>;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== list.data || $[26] !== t || $[27] !== threads.length) {
    t11 = list.data && !threads.length && <Empty icon="chats" title={t("chat.none")} text={t("chat.noneText")} />;
    $[25] = list.data;
    $[26] = t;
    $[27] = threads.length;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== list.error) {
    t12 = list.error && <ErrorLine>{list.error}</ErrorLine>;
    $[29] = list.error;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== t10 || $[32] !== t11 || $[33] !== t12 || $[34] !== t5 || $[35] !== t6 || $[36] !== t8) {
    t13 = <section className="page list chats" data-t="chats" data-page="chats" aria-label={t5} aria-busy={t6}>{t8}{t10}{t11}{t12}</section>;
    $[31] = t10;
    $[32] = t11;
    $[33] = t12;
    $[34] = t5;
    $[35] = t6;
    $[36] = t8;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  return t13;
}